import trim from 'lodash-es/trim';
import type { RouteLocationNormalizedLoaded as Route } from 'vue-router';

export class SitemapOptions
{
    public sitemap: SitemapNode[];
}

class SitemapLoader
{
    private sitemap: SitemapNode[] = [];
    private loaders: ((builder: SitemapBuilder) => Promise<void>)[] = [];

    public push(loader: (builder: SitemapBuilder) => Promise<void>)
    {
        this.loaders.push(loader);
    }

    public async load()
    {
        this.sitemap = [];

        const builder = new SitemapBuilder(this.sitemap);

        for (const loader of this.loaders)
        {
            await loader(builder);
        }

        return this.sitemap;
    }
}

class SitemapProvider
{
    private loader: SitemapLoader = null;
    private permissions: PermissionsProvider = null;
    private sitemap: SitemapNode[] = null;

    public constructor(loader: SitemapLoader, permissions: PermissionsProvider)
    {
        this.loader = loader;
        this.permissions = permissions;
    }

    public purge(): void
    {
        this.sitemap = [];
        this.permissions.purge();
    }

    protected inspect(items: SitemapNode[], namespace: string = ''): Record<string, boolean>
    {
        let permissions: Record<string, boolean> = {};

        for (let i = 0; i < items.length; i++)
        {
            const item = items[i];
            const ns = item.namespace || namespace;

            if (item.auth && 'all' in item.auth)
            {
                (item.auth.all as string[]).forEach((p) =>
                {
                    permissions[trim(`${ns}.${p}`, '.')] = false;
                });
            }

            if (item.auth && item.auth.any)
            {
                (item.auth.any as string[]).forEach((p) =>
                {
                    permissions[trim(`${ns}.${p}`, '.')] = false;
                });
            }

            if (item.children)
            {
                permissions = Object.assign(permissions, this.inspect(item.children, ns));
            }
        }

        return permissions;
    }

    protected async verify(permissions: Record<string, boolean>): Promise<Record<string, boolean>>
    {
        return await this.permissions.get(Object.keys(permissions));
    }

    protected clone(items: SitemapNode[]): SitemapNode[]
    {
        return JSON.parse(JSON.stringify(items));
    }

    protected apply(items: SitemapNode[], permissions: Record<string, boolean>, namespace: string = ''): SitemapNode[]
    {
        for (let i = 0; i < items.length; i++)
        {
            const item = items[i];
            const ns = item.namespace || namespace;

            item.parent = item.parent || null;
            item.allowed = item.parent ? item.parent.allowed : true;
            item.visible = item.visible == undefined ? true : item.visible;
            item.children = item.children == undefined ? [] : item.children;

            if (item.allowed && item.auth && item.auth.all && item.auth.all.length > 0)
            {
                item.allowed = this.all((item.auth.all as string[]).map(p => trim(`${ns}.${p}`, '.')), permissions);
            }

            if (item.allowed && item.auth && item.auth.any && item.auth.any.length > 0)
            {
                item.allowed = this.any((item.auth.any as string[]).map(p => trim(`${ns}.${p}`, '.')), permissions);
            }

            if (item.children.length > 0)
            {
                item.children.forEach((p: any) =>
                {
                    p.parent = item;
                });
                this.apply(item.children, permissions, ns);
            }

            item.visible = item.allowed && this.visible(item);
        }

        return items;
    }

    private path(node: SitemapNode)
    {
        let path = '';
        let item = node;
        let i = 10;

        while (item != null)
        {
            path = `${item.name} / ${path}`;
            item = item.parent;
            i--;

            if (i <= 0) break;
        }

        return path;
    }

    protected all(required: string[], permissions: Record<string, boolean>): boolean
    {
        return required.every(p => permissions[p] == true);
    }

    protected any(required: string[], permissions: Record<string, boolean>): boolean
    {
        return required.some(p => permissions[p] == true);
    }

    protected visible(node: SitemapNode): boolean
    {
        return (node.route || node.url || node.header) ? node.visible : node.visible && this.visibleChildren(node);
    }

    protected visibleChildren(node: SitemapNode): boolean
    {
        return node.children && node.children.some((p: any) => !p.header && this.visible(p));
    }

    public async get(): Promise<SitemapNode[]>
    {
        this.sitemap = await this.loader.load();

        let permissions = this.inspect(this.sitemap);

        permissions = await this.verify(permissions);

        let items = this.clone(this.sitemap);

        items = this.apply(items, permissions);

        return items;
    }
}

class SitemapHelper implements Sitemap
{
    private reactiveData = reactive({ extraCrumb: '' });
    private provider: SitemapProvider;
    private sitemap: Promise<SitemapNode[]>;
    private callback: () => Promise<void>;

    public constructor(provider: SitemapProvider)
    {
        this.provider = provider;
    }

    public get extraCrumb(): string
    {
        return this.reactiveData.extraCrumb;
    }

    public set extraCrumb(value: string)
    {
        this.reactiveData.extraCrumb = value;
    }

    public append(name: string): void
    {
        this.extraCrumb = name;
    }

    public purge(): void
    {
        this.provider.purge();
        this.sitemap = null;
    }

    public async all(): Promise<SitemapNode[]>
    {
        if (!this.sitemap)
        {
            this.sitemap = this.provider.get();
        }

        return await this.sitemap;
    }

    public async find(route: Partial<Route>): Promise<SitemapNode>
    {
        const find = (nodes: SitemapNode[], route: Partial<Route>): SitemapNode =>
        {
            let result: SitemapNode = null;

            for (let i = 0; i < nodes.length; i++)
            {
                if (this.active(nodes[i], route))
                {
                    result = nodes[i];
                }
                else if (nodes[i].children)
                {
                    result = result || find(nodes[i].children, route);
                }

                if (result != null)
                {
                    break;
                }
            }

            return result;
        };

        return find(await this.all(), route);
    }

    public async crumbs(route: Route): Promise<SitemapNode[]>
    {
        const node = await this.find(route);

        return this.path(node);
    }

    public path(node: SitemapNode): SitemapNode[]
    {
        const nodes: SitemapNode[] = [];

        while (node)
        {
            nodes.push(node);
            node = node.parent;
        }

        return nodes.reverse();
    }

    public active(node: SitemapNode, route: Partial<Route>, recursive: boolean = false): boolean
    {
        if (route.fullPath && node.url && route.fullPath === node.url)
        {
            return true;
        }
        else if (route.name && node.route && node.route === route.name)
        {
            if (node.routeParams && route.params)
            {
                return Object.keys(node.routeParams).every((key) =>
                {
                    return key in route.params && JSON.stringify(node.routeParams[key]) == JSON.stringify(route.params[key]);
                });
            }

            return true;
        }
        else if (recursive == true && node.children.length > 0)
        {
            for (let i = 0; i < node.children.length; i++)
            {
                if (this.active(node.children[i], route, recursive))
                {
                    return true;
                }
            }
        }

        return false;
    }

    public url(node: SitemapNode, route: Route): any
    {
        if (node.visible && node.allowed)
        {
            if (node.route)
            {
                return { name: node.route, params: node.routeParams || {}, query: node.query || {} };
            }
            else if (node.url)
            {
                return node.url;
            }
            else
            {
                return this.url(node.children.filter(p => !p.header && p.visible).first(), route);
            }
        }

        return route.fullPath;
    }

    public async reload(): Promise<void>
    {
        if (this.callback)
        {
            await this.callback();
        }
    }

    public onReload(callback: () => Promise<void>): void
    {
        this.callback = callback;
    }
}

export default defineNuxtPlugin({
    name: 'sitemap',
    dependsOn: ['permissions'],
    async setup(nuxtApp)
    {
        const { $permissions, $config } = useNuxtApp();
        const files = import.meta.glob(`~~/**/shared/sitemap.ts`, { eager: true });
        const loader = new SitemapLoader();

        for (const layer of $config.public.layerPaths)
        {
            const prefix = `.${layer}/shared/`;
            const definitions = Object.entries(files)
                .filter(([path]) => path.startsWith(prefix))
                .map(([path, result]) => result as any);

            for (const definition of definitions)
            {
                if (definition.default.constructor.name == 'AsyncFunction')
                    loader.push(definition.default);
                else
                    loader.push(async b => definition.default(b));
            }
        }

        const provider = new SitemapProvider(loader, $permissions);
        const helper = new SitemapHelper(provider) as Sitemap;

        return {
            provide: {
                sitemap: helper
            }
        };
    }
});

declare module '@vue/runtime-core'
{
    interface ComponentCustomProperties
    {
        $sitemap: Sitemap;
    }
}
